import {isDevMode} from '@angular/core';
import {StorageKeys} from '@core/storage';
import {config} from '@cron/data-access';
import {
	BrowserOptions,
	browserTracingIntegration,
	dedupeIntegration,
	ErrorEvent,
	EventHint,
	functionToStringIntegration,
	httpClientIntegration,
	init,
	makeBrowserOfflineTransport,
	makeFetchTransport,
	Scope,
} from '@sentry/angular';
import {environment} from '@shared-types';
import {storageSignal} from '@shared/signals-extension';

const allowDebuggingSentry$ = storageSignal(StorageKeys.Logging.SENTRY_DEBUG, false);

/**
 * Filter function for Sentry events before sending
 * Reduces event size by removing unnecessary information
 */
const beforeSendCallback = (event: ErrorEvent, hint: EventHint): ErrorEvent | PromiseLike<ErrorEvent> => {
	try {
		// Check if event is too large (over 150KB)
		const eventSize = JSON.stringify(event).length;
		if (eventSize > 150000) {
			// 150KB threshold
			console.debug(`[Sentry] Large event detected (${Math.round(eventSize / 1024)}KB), optimizing...`);

			// Reduce HTTP request information
			if (event.request) {
				// Remove large headers and data from requests
				if (event.request.headers && Object.keys(event.request.headers).length > 10) {
					event.request.headers = {
						'content-type': event.request.headers['content-type'],
						'user-agent': event.request.headers['user-agent'],
					};
				}

				// Limit data size
				if (event.request.data && JSON.stringify(event.request.data).length > 1000) {
					event.request.data = '[Data truncated due to size]';
				}
			}

			// Reduce breadcrumbs information
			if (event.breadcrumbs && event.breadcrumbs.length > 0) {
				// Keep only recent breadcrumbs
				if (event.breadcrumbs.length > 15) {
					event.breadcrumbs = event.breadcrumbs.slice(-15);
				}

				// Reduce data size in each breadcrumb
				event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
					if (breadcrumb.data && Object.keys(breadcrumb.data).length > 0) {
						const dataSize = JSON.stringify(breadcrumb.data).length;
						if (dataSize > 1000) {
							breadcrumb.data = {
								_truncated: true,
								originalSize: dataSize,
							};
						}
					}
					return breadcrumb;
				});
			}

			// Reduce contexts information
			if (event.contexts) {
				// Remove large or non-essential contexts
				const keysToKeep = ['browser', 'device', 'os', 'user'];
				Object.keys(event.contexts).forEach(key => {
					if (!keysToKeep.includes(key)) {
						delete event.contexts[key];
					}
				});
			}

			// Reduce spans information (for tracing)
			if (event.spans && event.spans.length > 0) {
				// Keep only important spans
				if (event.spans.length > 20) {
					// Keep spans with errors or especially long spans
					const importantSpans = event.spans.filter(
						span => span.status === 'error' || (span.data && typeof span.data.statusCode === 'number' && span.data.statusCode >= 400) || span.timestamp - span.start_timestamp > 1,
					);

					// If there are enough important spans, use them, otherwise take the latest ones
					event.spans = importantSpans.length >= 5 ? importantSpans.slice(0, 20) : event.spans.slice(-20);
				}

				// Reduce data in each span
				event.spans.forEach(span => {
					if (span.data && Object.keys(span.data).length > 5) {
						// Keep only essential information
						const keysToKeep = ['method', 'statusCode', 'url', 'type', 'error'];
						Object.keys(span.data).forEach(key => {
							if (!keysToKeep.includes(key)) {
								delete span.data[key];
							}
						});
					}
				});
			}
		}

		return event;
	} catch (error) {
		console.error('[Sentry] Error in beforeSend callback:', error);
		return event; // In case of error, return the original event
	}
};

export const initSentryConfig = async () => {
	const sentryConfig: BrowserOptions = {
		dsn: 'https://763d021eb71715a6e6ec33cf4da5f2e6@o1077959.ingest.us.sentry.io/4507729691738112',
		integrations: [dedupeIntegration, functionToStringIntegration, httpClientIntegration],
		debug: isDevMode() && allowDebuggingSentry$(),
		release: config.version,
		environment: `${isDevMode() ? `local-${environment.name}` : environment.name}`,
		maxValueLength: 100, // Reduce value length limit
		sendDefaultPii: false, // Disable sending personal information
		maxBreadcrumbs: 20, // Limit number of breadcrumbs
		normalizeDepth: 3, // Limit normalization depth
		beforeSend: beforeSendCallback, // Add filtering function
	};
	/* ------------------------------------------ Add Sentry Features: ------------------------------------------ */
	addEnvironmentTags(sentryConfig);
	// Sentry Tracing:
	useTracing(sentryConfig);
	// Sentry Offline Transport:
	useOfflineTransport(sentryConfig);

	init(sentryConfig);
	console.debug('[Sentry] Initialized with optimized configuration');
	return Promise.resolve();
};

/* -------------------------------------------------------------------------------------------------------------- */
const addEnvironmentTags = (sentryConfig: BrowserOptions) => {
	sentryConfig.initialScope = (scope: Scope) => {
		scope.setTag('environment', sentryConfig.environment);
		scope.setTag('sw:version', localStorage.getItem(StorageKeys.GlobalKeys.SW_VERSION) || 'N/A');
		scope.setTag('session_start', new Date().toISOString());
		return scope;
	};
};

const useTracing = (sentryConfig: BrowserOptions) => {
	sentryConfig.integrations = integrations => {
		integrations.push(
			browserTracingIntegration({
				idleTimeout: 1000,
				finalTimeout: 30000,
			}),
		);
		return integrations;
	};

	sentryConfig.tracesSampleRate = isDevMode() ? 0.5 : 0.2;
	sentryConfig.tracePropagationTargets = ['localhost', /^https:\/\/api\.doccycle\.com/]; // Limit propagation targets
};

const useOfflineTransport = (sentryConfig: BrowserOptions) => {
	sentryConfig.transport = makeBrowserOfflineTransport(makeFetchTransport);
	sentryConfig.transportOptions = {
		bufferSize: 50, // Reduce buffer size from 100 to 50
	};
};
